// Sheet's `height`: the two named stops, and what the prop accepts.
//
// Beside the component rather than inside it for the reason bidCardShapes.js
// gives — `defineProps()` is hoisted out of `setup()` and cannot reference a
// local declaration, so a validator that consults a map has to be imported.

/**
 * The named resting heights.
 *
 * `medium` and `large` are Apple's own two names for a sheet's detent, which is
 * the word this component deliberately stopped teaching: `height` says the same
 * thing in the language of the person reading the call site. `large` stops at
 * 88vh to match `.sheet`'s own max-height cap, and both resolve against the
 * viewport, which is the only thing a sheet is ever measured against.
 *
 * Both are rounded to a whole pixel because a bare `vh` fraction usually is
 * not one: 88vh of a 720px viewport is 633.6px, which lands the panel's top
 * border on a half pixel, and Chromium antialiases that edge differently from
 * one render to the next. The visual gate flipped between the two renderings
 * often enough to be re-blessed three times before the rounding fixed it.
 *
 * `medium` has no call site — every sheet that sets a height asks for `large`.
 * It is kept because it costs one entry and a confirmation over a tall page is
 * a real use for it, but it is a value to watch rather than one with a
 * constituency: on iOS the medium detent earns its keep in the undimmed case,
 * where it is the height that leaves the map visible behind the sheet, and
 * every sheet here is modal.
 */
export const NAMED_HEIGHTS = Object.freeze({
  medium: 'round(50vh, 1px)',
  large: 'round(88vh, 1px)',
});

/** A CSS length or percentage — the open half of the accepted set. */
const CSS_LENGTH = /^-?[\d.]+(?:px|%|r?em|v[hwib]|dv[hw]|sv[hw]|lv[hw]|cq[hwbi]|ch|ex|cm|mm|in|pt|pc)$/;

/**
 * Whether a value is one Sheet can rest at.
 *
 * The set is open at one end, which is why the prop went without a validator
 * for as long as it did: `auto` and the two names are closed, and beyond them
 * any explicit CSS length is legitimate for a sheet that needs a stop of its
 * own. Admitting both is what the validator has to do; admitting anything is
 * what it stopped doing.
 */
export function isSheetHeight(value) {
  return value === 'auto' || value in NAMED_HEIGHTS || CSS_LENGTH.test(value);
}
